import React, {
    Component
} from 'react';
import {
    Button,
    Form,
    TextField
} from '@vitalsource/vstui';
import '../../styles/TwoFactorAuthForm.css';
import studioLoginLogo from "../../img/login-logo.svg";
import styled from "styled-components";

const TwoFactorAuthFormContainer = styled(Form)`
  font-family: "Roboto";
  text-align: center;
`

const TwoFactorAuthFormTextField = styled(TextField)`
  width: 290px;
  display: block;
  margin-bottom: 24px;
  margin-left: 4px;
  > input {
    height: 42px;
    width: 100%;
    box-sizing: border-box;
    background-size: 14px 24px;
  }
  input:-webkit-autofill {
    -webkit-box-shadow: 0 0 0 2rem #e8f0fe inset;
  }
  > input[aria-invalid="true"]:focus {
    box-shadow: none !important;
  }
`;

interface TwoFactorAuthFormState {
    otpCode: string;
    hasError: boolean;
    errorType: 'empty' | 'incomplete' | null;
}

export interface TwoFactorAuthFormProps {
    verifyOtp?: (otpCode: string) => void;
    onClearMessage?: () => void;
    errorMessage?: string | null;
    loading?: boolean;
    isSubmitted?: boolean;
}

export default class TwoFactorAuthForm extends Component<TwoFactorAuthFormProps, TwoFactorAuthFormState> {
    constructor(props: TwoFactorAuthFormProps) {
        super(props);

        this.state = {
            otpCode: '',
            hasError: false,
            errorType: null
        };

        this.handleFieldChange = this.handleFieldChange.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    componentWillUnmount() {
        if (this.props.onClearMessage) {
            this.props.onClearMessage();
        }
    }

    handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        const { otpCode } = this.state;

        if (otpCode.length === 0) {
            this.setState({ hasError: true, errorType: 'empty' });
            return;
        }

        if (otpCode.length < 6) {
            this.setState({ hasError: true, errorType: 'incomplete' });
            return;
        }

        if (!this.props.isSubmitted && this.props.verifyOtp) {
            this.props.verifyOtp(otpCode);
        }
    }

    handleFieldChange(e: React.ChangeEvent<HTMLInputElement>) {
        const numericValue = e.target.value.replace(/[^0-9]/g, '').substring(0, 6);

        if (this.props.errorMessage && this.props.onClearMessage) {
            this.props.onClearMessage();
        }

        this.setState({
            otpCode: numericValue,
            hasError: false,
            errorType: null
        });
    }

    showIfError(): JSX.Element | null {
        const { errorMessage } = this.props;
        const { hasError, errorType } = this.state;

        let message = errorMessage;
        let className = "twofactor-error-message";

        if (hasError && errorType) {
            switch (errorType) {
                case 'empty':
                    message = "Please fill out this field.";
                    break;
                case 'incomplete':
                    message = "Please enter a 6-digit code.";
                    className = "twofactor-error-message twofactor-error-incomplete";
                    break;
            }
        }
        
        // Apply specific padding for session expired message
        if (errorMessage && errorMessage.toLowerCase().includes('session')) {
            className = "twofactor-error-message twofactor-error-session";
        }

        return message ? <div className={className}>{message}</div> : null;
    }

    render() {
        const hasError = this.state.hasError || !!this.props.errorMessage;
        const isLoading = this.props.loading || false;

        return (
            <div className="two-factor-auth-form">
                <TwoFactorAuthFormContainer
                    id="twoFactorAuthForm"
                    className="vstui-form"
                    onSubmit={this.handleSubmit}
                    noValidate
                >
                    {/* Studio Logo */}
                    <img className="twofactor-logo" src={studioLoginLogo} alt="Studio" />

                    {/* Subtitle */}
                    <h2 className="twofactor-subtitle">Two Factor Authentication</h2>

                    {/* Instructions */}
                    <p className="twofactor-instructions">
                        Enter the code from your authenticator app
                    </p>

                    {/* Error Message */}
                    {this.showIfError()}

                    {/* OTP Input Field */}
                    <TwoFactorAuthFormTextField
                        id="otpCode"
                        name="otpCode"
                        size="medium"
                        placeholder="Enter 6-digit code"
                        onChange={this.handleFieldChange}
                        value={this.state.otpCode}
                        required
                        type="text"
                        className={hasError ? 'twofactor-input-error' : ''}
                        inputMode="numeric"
                        maxLength={6}
                    />

                    {/* Submit Button + Spinner */}
                    <div className="twofactor-button-wrapper">
                        <Button
                            fullWidth
                            label="Submit"
                            size="large"
                            type="submit"
                        >
                            Submit
                        </Button>
                        {isLoading &&
                            <img
                                className="twofactor-spinner"
                                src="data:image/gif;base64,R0lGODlhEAAQAPIAAP///wAAAMLCwkJCQgAAAGJiYoKCgpKSkiH/C05FVFNDQVBFMi4wAwEAAAAh/hpDcmVhdGVkIHdpdGggYWpheGxvYWQuaW5mbwAh+QQJCgAAACwAAAAAEAAQAAADMwi63P4wyklrE2MIOggZnAdOmGYJRbExwroUmcG2LmDEwnHQLVsYOd2mBzkYDAdKa+dIAAAh+QQJCgAAACwAAAAAEAAQAAADNAi63P5OjCEgG4QMu7DmikRxQlFUYDEZIGBMRVsaqHwctXXf7WEYB4Ag1xjihkMZsiUkKhIAIfkECQoAAAAsAAAAABAAEAAAAzYIujIjK8pByJDMlFYvBoVjHA70GU7xSUJhmKtwHPAKzLO9HMaoKwJZ7Rf8AYPDDzKpZBqfvwQAIfkECQoAAAAsAAAAABAAEAAAAzMIumIlK8oyhpHsnFZfhYumCYUhDAQxRIdhHBGqRoKw0R8DYlJd8z0fMDgsGo/IpHI5TAAAIfkECQoAAAAsAAAAABAAEAAAAzIIunInK0rnZBTwGPNMgQwmdsNgXGJUlIWEuR5oWUIpz8pAEAMe6TwfwyYsGo/IpFKSAAAh+QQJCgAAACwAAAAAEAAQAAADMwi6IMKQORfjdOe82p4wGccc4CEuQradylesojEMBgsUc2G7sDX3lQGBMLAJibufbSlKAAAh+QQJCgAAACwAAAAAEAAQAAADMgi63P7wCRHZnFVdmgHu2nFwlWCI3WGc3TSWhUFGxTAUkGCbtgENBMJAEJsxgMLWzpEAACH5BAkKAAAALAAAAAAQABAAAAMyCLrc/jDKSatlQtScKdceCAjDII7HcQ4EMTCpyrCuUBjCYRgHVtqlAiB1YhiCnlsRkAAAOwAAAAAAAAAAAA=="
                                alt="Loading..."
                            />
                        }
                    </div>

                    {/* Help Link */}
                    <div className="link-context">
                        <span>Need help? </span>
                        <a
                            className="two-factor-auth-form-link"
                            href="https://success.vitalsource.com/hc/en-us/articles/32299550221591-Two-factor-authentication-2FA-for-Manage"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            View our documentation on 2FA
                        </a>
                    </div>
                </TwoFactorAuthFormContainer>
            </div>
        )
    }
}
